<template>
  <div>
    <v-alert
      v-if="filters.dates.to && filters.dates.from !== filters.dates.to"
      color="error"
      type="error"
      dark
      class="mt-2"
    >
      {{ $t("daily_report") }}
    </v-alert>
    <template v-else>
      <v-row justify="end" align="end" class="mb-2">
        <v-col cols="auto">
          <text-field
            v-model="search"
            append-icon="mdi-magnify"
            :placeholder="$t('search')"
            hide-details
          ></text-field>
        </v-col>
        <v-col cols="auto">
          <dropdown
            :label="$t('view')"
            v-model="selectedLayout"
            :items="layouts"
            hide-details
          ></dropdown>
        </v-col>
        <v-col cols="auto">
          <shift-picker v-model="selectedShift" :shifts="shifts" />
        </v-col>
      </v-row>
      <v-data-table
        :headers="headers"
        :items="customers"
        :loading="dayLoading"
        class="striped-table ordera-box-shadow"
      >
        <template v-slot:item="{ item, index }">
          <tr>
            <td>{{ index + 1 }}</td>
            <td>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    {{ item.fullname }}
                  </span>
                </template>
                <span>{{ item.userId }}</span>
              </v-tooltip>
            </td>
            <td>{{ item.date }}</td>
            <td>{{ item.shift }}</td>
            <template v-for="category in mealCategories">
              <td :key="category">
                <template v-for="(meal, mealIndex) in item[category] || []">
                  <br v-if="mealIndex > 0" :key="`br_${mealIndex}`" />
                  <span :key="`meal_${mealIndex}`">
                    <!-- Layout 0: Količine i nazive jela -->
                    <template v-if="selectedLayout === 0">
                      <strong>{{ meal.quantity }}x</strong>
                      {{ toLatin(meal.name) }}
                    </template>
                    <!-- Layout 1: Nazive jela -->
                    <template v-else-if="selectedLayout === 1">
                      {{ toLatin(meal.name) }}
                    </template>
                    <!-- Layout 2: Količine jela -->
                    <template v-else-if="selectedLayout === 2">
                      <strong>{{ meal.quantity }}</strong>
                    </template>
                  </span>
                </template>
              </td>
            </template>
            <td>{{ item.note }}</td>
          </tr>
        </template>
        <template v-slot:body.append>
          <tr style="background: #f5f5f5; font-weight: bold">
            <td></td>
            <td>{{ $t("total") }}</td>
            <td></td>
            <td></td>
            <template v-for="category in mealCategories">
              <td :key="category">{{ getTotal(category) }}x</td>
            </template>
            <td></td>
          </tr>
        </template>
      </v-data-table>
    </template>
  </div>
</template>

<script>
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";
import hasFoodKorisniciByDay from "@/mixins/report/hasFoodKorisniciByDay";
export default {
  name: "CompanyReportTabCustomers",
  mixins: [hasReportPrinting, hasPrinting, hasExcel, hasFoodKorisniciByDay],
  data() {
    return {
      selectedShift: null,
      selectedLayout: 0,
      search: "",
      searchDebounced: "",
      searchTimeout: null,
    };
  },
  computed: {
    layouts() {
      return [
        { text: this.$t("quantities_and_names"), value: 0 },
        { text: this.$t("names_only"), value: 1 },
        { text: this.$t("quantities_only"), value: 2 },
        { text: this.$t("spare_meals"), value: 3 },
      ];
    },
    // Kolone grupa jela su po category_id
    mealCategories() {
      return this.dayCategoryList.map((category) => category.id);
    },
    // Jedan red po korisniku, danu i smeni; u ćeliji grupe je lista jela
    allCustomers() {
      const byKey = new Map();
      this.dayRows.forEach((row) => {
        const key = [row.user_id, row.date, row.shift].join("|");
        if (!byKey.has(key)) {
          byKey.set(key, {
            userId: row.user_id,
            name: row.name,
            lastname: row.lastname,
            date: row.date,
            shift: row.shift,
            note: row.note || "",
          });
        }
        const customer = byKey.get(key);
        if (!customer.note && row.note) customer.note = row.note;
        (customer[row.category_id] = customer[row.category_id] || []).push({
          quantity: row.quantity,
          name: row.meal_name,
          id: row.food_id,
        });
      });
      return [...byKey.values()];
    },
    customers() {
      let filteredCustomers = this.allCustomers.filter(
        (customer) => customer.shift === this.selectedShift
      );

      // Primeni search filter ako postoji
      if (this.searchDebounced && this.searchDebounced.trim()) {
        const searchTerm = this.searchDebounced.toLowerCase().trim();
        filteredCustomers = filteredCustomers.filter((customer) => {
          const fullname = [customer.name, customer.lastname].join(" ").toLowerCase();
          const userId = (customer.userId || "").toString().toLowerCase();
          const note = (customer.note || "").toLowerCase();

          // Pretraži i po imenima jela
          const foodNames = this.mealCategories
            .flatMap((category) => customer[category] || [])
            .map((meal) => meal.name || "")
            .join(" ")
            .toLowerCase();

          return (
            fullname.includes(searchTerm) ||
            userId.includes(searchTerm) ||
            note.includes(searchTerm) ||
            foodNames.includes(searchTerm)
          );
        });
      }

      return filteredCustomers.map((customer) => ({
        ...customer,
        fullname: this.toLatin([customer.name, customer.lastname].join(" ")),
      }));
    },
    headers() {
      return [
        { text: this.$t("r_b"), value: "num" },
        { text: this.$t("name_surname"), value: "fullname" },
        { text: this.$t("date"), value: "date" },
        { text: this.$t("shift.shift"), value: "shift" },
        ...this.dayCategoryList.map((category) => ({
          text: this.dayCategoryLabels[category.id],
          value: String(category.id),
        })),
        { text: this.$t("note"), value: "note" },
      ];
    },
    selectedCompanies() {
      return this.$store.state.report.selectedFoodCompanies
        .map((company) => this.toLatin(company?.name || ""))
        .join(", ");
    },
    shifts() {
      return [...new Set(this.dayRows.map((row) => row.shift))].sort((a, b) => a - b);
    },
    filters() {
      return this.$store.state.report.filters;
    },
    totals() {
      const result = {};
      this.mealCategories.forEach((category) => {
        result[category] = this.customers.reduce((sum, customer) => {
          return (
            sum +
            (customer[category] || []).reduce((s, meal) => s + (meal.quantity || 0), 0)
          );
        }, 0);
      });
      return result;
    },
    company() {
      return this.$store.state.company;
    },
    // Redovi za štampu i Excel: ćelije grupa kao tekst prema izabranom prikazu
    exportCustomers() {
      return this.customers.map((customer, index) => {
        const row = { num: index + 1, ...customer };
        this.mealCategories.forEach((category) => {
          row[category] = (customer[category] || [])
            .map((meal) => {
              const name = this.toLatin(meal.name || "");
              if (this.selectedLayout === 1) return name;
              if (this.selectedLayout === 2) return meal.quantity;
              return [meal.quantity, name].join("x ");
            })
            .join(", ");
        });
        return row;
      });
    },
  },
  methods: {
    getTotal(category) {
      return this.totals[category] || 0;
    },
  },
  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },
  watch: {
    search: {
      handler(newSearch) {
        // Debounce search za 300ms
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
          this.searchDebounced = newSearch;
        }, 300);
      },
    },
    shifts: {
      handler() {
        if (this.shifts.length) {
          this.selectedShift = this.shifts[0];
        } else {
          this.selectedShift = null;
        }
      },
      immediate: true,
    },
    isPrinting() {
      if (this.isPrinting) {
        this.printDocument({
          tables: [
            {
              headers: this.headers,
              items: this.exportCustomers,
            },
          ],
          title: [this.$t("report"), this.$t("orderers")].join(" "),
          subtitle: this.subtitle,
          beforeTableHtml: `<h4 style="text-align: center">${this.selectedCompanies}</h4>`,
        });
      }
    },
    isExcelDownloading() {
      if (this.isExcelDownloading) {
        this.downloadExcel({
          fileName: [this.$t("report"), this.$t("orderers")].join(" ") + ".xlsx",
          sheets: [
            {
              name: [this.$t("report"), this.$t("orderers")].join(" "),
              headers: this.headers,
              items: this.exportCustomers,
              title: this.$t("users_and_ordered_meals"),
              subtitle: this.subtitle,
              beforeTableHtml: `<h4 style="text-align: center">${
                this.selectedCompanies || ""
              }</h4>`,
            },
          ],
        });
      }
    },
  },
};
</script>
